// src/pages/About.jsx
import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import 'swiper/css';
import 'swiper/css/autoplay';

// === IMAGES ===
import heroBg from '../assets/pick/20.jpg';
import slide1 from '../assets/pick2/sigiriya/pexels-nasti-362148574-18619138.jpg';
import slide2 from '../assets/pick2/yala/LK710A2062-02-E.webp';
import slide3 from '../assets/pick2/Nuware Eliya/istockphoto-579253918-612x612.jpg';
import slide4 from '../assets/pick2/sea/download (2).jpeg';
import slide5 from '../assets/pick2/Anuradapura/istockphoto-1255135539-612x612.jpg';

import tourImage1 from '../assets/pick2/Anuradapura/istockphoto-471199868-612x612.jpg';
import tourImage2 from '../assets/pick2/Anuradapura/istockphoto-824016044-612x612.jpg';
import tourImage3 from '../assets/pick2/polonnaruwa/istockphoto-1181095560-612x612.jpg';
import tourImage4 from '../assets/pick/21.jpg';
import tourImage5 from '../assets/pick/22.jpg';
import tourImage6 from '../assets/pick/23.jpeg';

import aboutImage from '../assets/pick/24.jpg';
import offer1 from '../assets/pick/25.jpg';
import offer2 from '../assets/pick/26.jpg';
import offer3 from '../assets/pick/556091366.webp';
import offer4 from '../assets/pick/27.jpg';

import ourGuide from '../assets/pick/28.jpg';
import tourExpert from '../assets/pick/29.jpg';


import van from '../assets/pick/30.jpg';
import minibus from '../assets/pick/31.jpg';
import luxury from '../assets/pick/32.webp';
import car from '../assets/pick/45.avif';

// === FRAMER MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};

const About = () => {
    const { t } = useTranslation();

    // === DATA ===
    const whoWeAreDetails = [
        {
            number: '01',
            title: t('about.whoWeAre.0.title'),
            description: t('about.whoWeAre.0.description')
        },
        {
            number: '02',
            title: t('about.whoWeAre.1.title'),
            description: t('about.whoWeAre.1.description')
        },
        {
            number: '03',
            title: t('about.whoWeAre.2.title'),
            description: t('about.whoWeAre.2.description')
        },
        {
            number: '04',
            title: t('about.whoWeAre.3.title'),
            description: t('about.whoWeAre.3.description')
        },
    ];

    const whatWeOfferDetails = [
        { image: offer1, title: t('about.whatWeOffer.0.title'), description: t('about.whatWeOffer.0.description') },
        { image: offer2, title: t('about.whatWeOffer.1.title'), description: t('about.whatWeOffer.1.description') },
        { image: offer3, title: t('about.whatWeOffer.2.title'), description: t('about.whatWeOffer.2.description') },
        { image: offer4, title: t('about.whatWeOffer.3.title'), description: t('about.whatWeOffer.3.description') },
    ];

    const heroImagesSlider = [
        { image: slide1, title: t('about.heroSlider.0') },
        { image: slide2, title: t('about.heroSlider.1') },
        { image: slide3, title: t('about.heroSlider.2') },
        { image: slide4, title: t('about.heroSlider.3') },
        { image: slide5, title: t('about.heroSlider.4') },
    ];

    const vehicles = [
        { type: t('about.vehicles.car.type'), image: car, description: t('about.vehicles.car.description') },
        { type: t('about.vehicles.van.type'), image: van, description: t('about.vehicles.van.description') },
        { type: t('about.vehicles.minibus.type'), image: minibus, description: t('about.vehicles.minibus.description') },
        { type: t('about.vehicles.luxury.type'), image: luxury, description: t('about.vehicles.luxury.description') },

    ];

    return (
        <div className="bg-black">
            {/* ===== HERO SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative max-h-[1400px] w-full bg-cover bg-center md:h-screen md:max-h-[740px] lg:max-h-[850px] xl:max-h-[1000px]"
                style={{
                    backgroundImage: `
                        linear-gradient(0deg, rgba(0, 0, 0, 0.67) 32.63%, rgba(0, 0, 0, 0.18) 63.34%),
                        url(${heroBg})
                    `,
                }}
            >
                <div className="container mx-auto px-4">
                    <div className="relative w-full">
                        <div className="relative w-fit pt-[120px] md:pt-[100px] lg:pt-[120px] xl:pt-[150px]">
                            <h1 className="font-bold leading-[70px] md:leading-[47px] lg:leading-[5.5rem]">
                                <span
                                    className="text-[3.125rem] md:text-5xl lg:text-7xl xl:text-[6.25rem]"
                                    style={{
                                        background: 'linear-gradient(90deg, #009CE6 0%, #96BA33 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                    }}
                                >
                                    {t('about.hero.explore')}
                                </span>
                                <br />
                                <span
                                    className="text-[4rem] md:text-[68px] lg:text-9xl xl:text-[9.375rem]"
                                    style={{
                                        background: 'linear-gradient(90deg, #96BA33 0%, #009CE6 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                    }}
                                >
                                    {t('about.hero.pickLanka')}
                                </span>
                            </h1>
                            <div className="absolute right-2 top-[180px] h-[5px] w-[100px] bg-white md:top-[125px] md:h-[4px] md:w-[105px] lg:top-[200px] lg:h-[5px] lg:w-[245px] xl:top-[230px]"></div>
                            <div className="absolute h-[5px] w-[110px] bg-white md:h-[4px] lg:h-[5px] lg:w-[230px] bottom-[-20px] left-0"></div>
                        </div>

                        <div className="flex w-full flex-col md:items-end md:justify-end">
                            {/* HERO SLIDER */}
                            <>
                                {/* Desktop & Tablet */}
                                <div className="relative mt-10 hidden h-[210px] w-[580px] overflow-hidden rounded-lg shadow-lg md:block lg:h-[280px] lg:w-[850px] xl:h-[330px] xl:w-[920px]">
                                    <Swiper
                                        modules={[Autoplay]}
                                        slidesPerView={4}
                                        spaceBetween={12}
                                        loop
                                        speed={500}
                                        autoplay={{ delay: 2500, disableOnInteraction: false, pauseOnMouseEnter: true }}
                                        breakpoints={{
                                            768: { slidesPerView: 3 },
                                            1024: { slidesPerView: 4 },
                                        }}
                                        className="h-full"
                                    >
                                        {heroImagesSlider.map((slide, i) => (
                                            <SwiperSlide key={i} className="!h-full">
                                                <div className="relative h-full w-full">
                                                    <img src={slide.image} alt={slide.title} className="h-full w-full rounded-[5px] object-cover" />
                                                    <h4 className="absolute bottom-5 left-5 max-w-[80px] font-medium text-white text-lg lg:max-w-[130px] lg:text-xl xl:max-w-[150px]">
                                                        {slide.title}
                                                    </h4>
                                                </div>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>

                                {/* Mobile */}
                                <div className="relative mt-10 h-[280px] w-full overflow-hidden rounded-lg shadow-lg md:hidden">
                                    <Swiper
                                        modules={[Autoplay]}
                                        slidesPerView={2}
                                        spaceBetween={12}
                                        loop
                                        speed={500}
                                        autoplay={{ delay: 2500, disableOnInteraction: false, pauseOnMouseEnter: true }}
                                        className="h-full"
                                    >
                                        {heroImagesSlider.map((slide, i) => (
                                            <SwiperSlide key={i} className="!h-full">
                                                <div className="relative h-full w-full">
                                                    <img src={slide.image} alt={slide.title} className="h-full w-full rounded-[5px] object-cover" />
                                                    <h4 className="absolute bottom-3 left-5 font-medium text-white text-xl">
                                                        {slide.title}
                                                    </h4>
                                                </div>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            </>

                            {/* Text blocks */}
                            <div className="relative z-10 hidden w-[210px] text-right md:block lg:w-[334px]">
                                <p className="mt-8 text-white text-sm font-medium lg:text-base xl:mt-10">
                                    {t('about.hero.text1')}
                                </p>
                                <div className="absolute -bottom-3 right-0 h-1 w-[100px] bg-[#9D9D9D] lg:w-[150px]"></div>
                            </div>

                            <div className="relative bottom-7 left-0 z-10 mt-14 w-[244px] text-left md:absolute md:mt-10 md:w-[205px] lg:bottom-28 lg:w-[320px] xl:w-[353px]">
                                <p className="text-white text-xs leading-5 font-medium md:text-sm md:leading-normal lg:text-base">
                                    {t('about.hero.text2')}
                                </p>
                                <div className="absolute -bottom-3 left-0 h-1 w-[100px] bg-[#9D9D9D] lg:w-[150px]"></div>
                            </div>

                            <div className="relative z-10 flex w-full items-end justify-end text-right md:hidden lg:w-[334px]">
                                <p className="mt-8 w-[220px] text-white text-xs leading-5 font-medium md:text-sm lg:text-base xl:mt-10">
                                    {t('about.hero.text1')}
                                </p>
                                <div className="absolute -bottom-3 right-0 h-1 w-[100px] bg-[#9D9D9D] lg:w-[150px]"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ===== ABOUT PICK LANKA SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black py-20 "
            >
                <div className="container mx-auto px-4">
                    <div className="text-center">
                        <h2 className="font-konkhmer text-4xl font-bold uppercase md:text-5xl lg:text-6xl">
                            <span
                                style={{
                                    background: 'linear-gradient(to right, #009CE6, #96BA33)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}
                            >
                                {t('about.sectionTitle')}
                            </span>
                        </h2>
                        <div className="mx-auto mt-3 h-[3px] w-24 bg-[#009CE6] lg:w-32"></div>
                        <div className="mx-auto mt-1 h-[3px] w-16 bg-[#96BA33] lg:w-24"></div>
                    </div>

                    <div className="relative mt-7 h-[1140px] w-full rounded-[30px] border border-[#009CE6] bg-gradient-to-br from-[#1a1a1a] to-[#111] p-8 shadow-xl md:h-[700px] lg:mt-10 lg:h-[970px] lg:p-12">
                        <div className="flex w-full flex-col items-center">
                            <h3 className="text-center text-[#009CE6] text-2xl font-bold md:text-3xl lg:text-4xl xl:text-[2.5rem]">
                                {t('about.signatureTour.title')}
                            </h3>
                            <div className="mt-2 h-[4px] w-[80px] bg-[#96BA33] md:w-[140px] lg:h-[5px] lg:w-[170px] xl:w-[180px]"></div>
                        </div>

                        {/* Tour Images – alt text is translated */}
                        <img src={tourImage1} alt={t('about.signatureTour.images.0')} className="absolute left-[220px] top-[130px] h-[94px] w-[130px] rounded-[15px] object-cover md:left-[280px] md:top-[150px] md:h-[75px] md:w-[75px] lg:left-[580px] lg:top-[170px] lg:h-[150px] lg:w-[150px] xl:left-[620px] xl:h-[162px] xl:w-[162px]" />
                        <img src={tourImage2} alt={t('about.signatureTour.images.1')} className="absolute left-[20px] top-[250px] h-[93px] w-[132px] rounded-[15px] object-cover md:top-[180px] md:h-[90px] md:w-[110px] lg:left-[80px] lg:top-[230px] lg:h-[150px] lg:w-[186px] xl:left-[100px] xl:h-[200px] xl:w-[250px]" />
                        <img src={tourImage3} alt={t('about.signatureTour.images.2')} className="absolute left-[75px] top-[320px] h-[84px] w-[92px] rounded-[15px] object-cover md:left-[100px] md:top-[250px] md:h-[75px] md:w-[90px] lg:left-[200px] lg:top-[320px] lg:h-[120px] lg:w-[150px] xl:left-[250px] xl:top-[370px] xl:h-[160px] xl:w-[200px]" />
                        <img src={tourImage4} alt={t('about.signatureTour.images.3')} className="absolute left-[215px] top-[470px] h-[105px] w-[130px] rounded-[15px] object-cover md:left-[310px] md:top-[380px] md:h-[76px] md:w-[90px] lg:left-[610px] lg:top-[540px] lg:h-[126px] lg:w-[156px] xl:left-[650px] xl:top-[560px] xl:h-[170px] xl:w-[210px]" />
                        <img src={tourImage5} alt={t('about.signatureTour.images.4')} className="absolute right-[10px] top-[540px] h-[80px] w-[98px] rounded-[15px] object-cover md:right-[300px] md:top-[430px] md:h-[65px] md:w-[75px] lg:right-[380px] lg:top-[630px] lg:h-[96px] lg:w-[120px] xl:left-[750px] xl:top-[690px] xl:h-[130px] xl:w-[160px]" />
                        <img src={tourImage6} alt={t('about.signatureTour.images.5')} className="absolute left-[40px] top-[530px] h-[80px] w-[98px] rounded-[15px] object-cover md:left-[80px] md:top-[500px] md:h-[63px] md:w-[73px] lg:left-[210px] lg:top-[740px] lg:h-[96px] lg:w-[120px] xl:top-[760px] xl:h-[130px] xl:w-[160px]" />

                        {/* Timeline dots */}
                        <div className="absolute left-[175px] top-[200px] flex flex-col items-center gap-3 md:left-[240px] lg:left-[490px] lg:top-[240px] xl:left-[530px]">
                            <div className="h-4 w-4 rounded-full bg-[#009CE6] shadow-md"></div>
                            <div className="h-16 w-1 bg-[#009CE6] opacity-50"></div>
                            <div className="h-4 w-4 rounded-full bg-[#009CE6] shadow-md"></div>
                            <div className="h-16 w-1 bg-[#009CE6] opacity-50"></div>
                            <div className="h-4 w-4 rounded-full bg-[#009CE6] shadow-md"></div>
                        </div>

                        {/* Labels */}
                        <div className="absolute left-[80px] top-[180px] flex flex-col items-center md:left-[140px] lg:left-[340px] lg:top-[220px] xl:left-[370px]">
                            <h4 className="text-[#009CE6] text-lg font-semibold">{t('about.signatureTour.labels.0.days')}</h4>
                            <h4 className="text-white text-xl font-bold">{t('about.signatureTour.labels.0.title')}</h4>
                        </div>
                        <div className="absolute left-[210px] top-[400px] flex flex-col items-center md:left-[285px] md:top-[310px] lg:left-[560px] lg:top-[450px] xl:left-[600px]">
                            <h4 className="text-[#009CE6] text-lg font-semibold">{t('about.signatureTour.labels.1.days')}</h4>
                            <h4 className="text-white text-xl font-bold">{t('about.signatureTour.labels.1.title')}</h4>
                        </div>
                        <div className="absolute left-[80px] top-[630px] flex flex-col items-center md:left-[150px] md:top-[460px] lg:left-[330px] lg:top-[700px] xl:left-[370px]">
                            <h4 className="text-[#009CE6] text-lg font-semibold">{t('about.signatureTour.labels.2.days')}</h4>
                            <h4 className="text-white text-xl font-bold">{t('about.signatureTour.labels.2.title')}</h4>
                        </div>

                        {/* Descriptions */}
                        <p className="absolute right-5 top-[730px] w-[320px] text-right text-sm leading-relaxed text-gray-300 md:top-[160px] lg:right-12 lg:top-[240px] xl:text-base">
                            {t('about.signatureTour.desc1')}
                        </p>

                        <p className="absolute left-5 top-[870px] text-left text-sm leading-relaxed text-gray-300 md:left-auto md:right-[70px] md:top-[310px] md:w-[390px] md:text-right lg:right-[160px] lg:top-[440px] xl:right-[300px] xl:text-base">
                            {t('about.signatureTour.desc2')}
                        </p>

                        <p className="absolute right-5 top-[1020px] w-[320px] text-right text-sm leading-relaxed text-gray-300 md:right-10 md:top-[550px] lg:top-[700px] xl:w-[420px] xl:text-base">
                            {t('about.signatureTour.desc3')}
                        </p>
                    </div>
                </div>
            </motion.section>

            {/* ===== WHO WE ARE – DESKTOP/TABLET ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black py-12 md:block hidden"
            >
                <div className="container mx-auto px-4">
                    <div className="grid w-full grid-cols-1 gap-3 gap-y-20 md:grid-cols-4 md:gap-y-3 lg:gap-8 lg:gap-y-8">
                        {whoWeAreDetails.map((item) => (
                            <div key={item.number} className="relative flex h-[200px] w-full flex-col items-center text-center md:h-[220px]">
                                <span className="pointer-events-none absolute -top-24 left-1/2 z-0 -translate-x-1/2 font-redHatDisplay text-[6.25rem] font-bold text-[#E6C445] opacity-90 md:-top-[40px] md:text-6xl lg:-top-[75px] lg:text-[5rem] xl:-top-[90] xl:text-[6.25rem]">
                                    {item.number}

                                </span>
                                <br />
                                <div className="relative z-10 h-auto w-full border border-[#E6C445] bg-[#111] p-5 md:p-4 lg:p-6">
                                    <h3 className="font-redHatDisplay text-2xl font-semibold text-[#E6C445] md:text-lg lg:text-xl xl:text-2xl">
                                        {item.title}
                                    </h3>
                                    <p className="mt-4 text-lg font-medium text-white md:text-sm lg:mt-5 lg:text-lg xl:text-xl">
                                        {item.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 flex w-full flex-col items-start justify-between md:flex-row md:items-center lg:mt-20">
                        <div className="w-full md:w-[38%]">
                            <h2 className="font-konkhmer text-4xl font-semibold lg:text-5xl xl:text-6xl text-white">
                                {t('about.whoWeAre.title')}
                            </h2>
                            <p className="mt-7 font-redHatDisplay text-sm font-medium leading-8 text-white/80 md:text-base lg:text-lg xl:text-xl">
                                {t('about.whoWeAre.description')}
                            </p>
                        </div>
                        <div className="mt-10 w-full md:mt-0 md:w-[45%]">
                            <img src={aboutImage} alt={t('about.whoWeAre.imageAlt')} className="h-[450px] w-full rounded-[20px] object-cover md:h-[500px] lg:h-[600px] xl:h-[687px]" />
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ===== WHO WE ARE – MOBILE ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black py-12 md:hidden block"
            >
                <div className="container mx-auto px-4">
                    <div className="grid w-full grid-cols-1 gap-6">
                        {whoWeAreDetails.map((item) => (
                            <div key={item.number} className="relative flex h-[180px] w-full flex-col items-center text-center">
                                <div className="relative z-10 h-full w-full border border-[#E6C445] bg-[#111] p-4">
                                    <h3 className="font-redHatDisplay text-xl font-semibold text-[#E6C445]">
                                        {item.title}
                                    </h3>
                                    <p className="mt-3 text-base font-medium text-white">
                                        {item.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 flex flex-col items-center text-center">
                        <h2 className="font-konkhmer text-4xl font-semibold text-white">
                            {t('about.whoWeAre.title')}
                        </h2>
                        <img
                            src={aboutImage}
                            alt={t('about.whoWeAre.imageAlt')}
                            className="mt-6 h-[400px] w-full rounded-[20px] object-cover"
                        />
                        <p className="mt-6 font-redHatDisplay text-base font-medium leading-7 text-white/80">
                            {t('about.whoWeAre.description')}
                        </p>
                    </div>
                </div>
            </motion.section>

            {/* ===== WHAT WE OFFER ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black py-20"
            >
                <div className="container mx-auto px-4">
                    <div className="text-center">
                        <h2 className="font-konkhmer text-4xl font-bold uppercase md:text-5xl lg:text-6xl">
                            <span
                                style={{
                                    background: 'linear-gradient(to right, #009CE6, #96BA33)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}
                            >
                                {t('about.whatWeOffer.title')}
                            </span>
                        </h2>
                        <div className="mx-auto mt-3 h-[3px] w-24 bg-[#009CE6] lg:w-32"></div>
                        <div className="mx-auto mt-1 h-[3px] w-16 bg-[#96BA33] lg:w-24"></div>
                    </div>

                    <div className="mt-12 flex w-full items-stretch gap-4 overflow-x-auto px-4 pb-5 md:grid md:grid-cols-4 md:pb-0 lg:mt-16 lg:gap-5 lg:px-5">
                        {whatWeOfferDetails.map((item) => (
                            <div key={item.title} className="relative h-[620px] w-[330px] shrink-0 overflow-clip rounded-[15px] md:h-[290px] md:w-auto lg:h-[480px] xl:h-[650px]">
                                <img src={item.image} className="h-full w-full object-cover" alt={item.title} />
                                <div className="absolute bottom-0 flex w-full flex-col items-center bg-gradient-to-t from-black/80 to-transparent px-3 py-6 md:py-4 lg:py-7">
                                    <h4 className="text-center font-redHatDisplay text-2xl font-bold text-white md:text-sm lg:text-xl xl:text-2xl">
                                        {item.title}
                                    </h4>
                                    <h5 className="mt-3 text-center font-redHatDisplay text-lg font-medium text-white md:text-xs lg:mt-5 lg:text-lg">
                                        {item.description}
                                    </h5>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </motion.section>

            {/* ===== OUR GUIDES ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black"
            >
                <div className="container mx-auto px-4">
                    <div className="flex w-full flex-col items-center justify-between md:flex-row">
                        <div className="flex w-[330px] flex-col items-center lg:w-[450px]">
                            <h2 className="font-konkhmer text-3xl font-semibold uppercase text-white md:text-4xl lg:text-[3.75rem]">
                                {t('about.ourGuides.title')}
                            </h2>
                            <p className="mt-7 text-center font-redHatDisplay text-sm font-medium text-white/80 md:text-lg lg:text-xl">
                                {t('about.ourGuides.description')}
                            </p>
                        </div>
                        <img src={ourGuide} alt={t('about.ourGuides.imageAlt')} className="mt-10 w-full md:mt-0 md:w-[340px] lg:w-[655px] xl:w-[755px]" />
                    </div>
                </div>
            </motion.section>

            {/* ===== TOUR EXPERT ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black py-20"
            >
                <div className="container mx-auto px-4">
                    <div className="flex w-full flex-col-reverse items-center justify-between gap-10 md:flex-row">
                        <img src={tourExpert} alt={t('about.tourExpert.imageAlt')} className="h-auto w-full overflow-clip rounded-[20px] object-contain md:w-[300px] lg:w-[450px] xl:w-[600px]" />
                        <div className="flex flex-col items-center md:items-end">
                            <h3 className="font-konkhmer text-3xl font-semibold uppercase text-white md:text-4xl lg:text-5xl xl:text-6xl">
                                {t('about.tourExpert.name')}
                            </h3>
                            <h4 className="mt-2 font-redHatDisplay text-sm font-semibold uppercase text-white md:text-2xl lg:text-3xl xl:text-4xl">
                                {t('about.tourExpert.role')}
                            </h4>
                            <div className="mt-5 h-1 w-[130px] bg-white lg:h-[5px] lg:w-[180px] xl:w-[245px]"></div>

                            <p className="mt-7 w-[380px] text-center font-redHatDisplay text-sm font-medium leading-8 text-white/80 md:text-right md:text-base lg:mt-10 lg:w-[442px] lg:text-lg lg:leading-9 xl:text-xl xl:leading-10">
                                {t('about.tourExpert.description')}
                            </p>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ===== VEHICLES ===== */}

            <div className="w-full max-w-[375px] justify-self-center md:max-w-[768px] md:pl-[16px] lg:max-w-[1280px] lg:pl-[40px] xl:max-w-[1728px] xl:pl-[70px]">
                <div className="flex w-full flex-col items-center justify-between gap-6 md:flex-row md:items-start lg:gap-10">
                    <div className="flex w-full flex-col items-start md:w-[30%] xl:w-[25%]">
                        <div className="flex items-start gap-3">
                            <h2 className="-mt-2 w-[230px] font-konkhmer text-4xl font-semibold uppercase leading-[60px] text-white md:w-auto md:leading-[50px] lg:text-5xl lg:leading-[60px] xl:text-[3.75rem] xl:leading-[75px]">
                                {t('about.vehicles.title')}
                            </h2>
                        </div>
                        <p className="mt-5 font-redHatDisplay text-sm font-medium leading-8 text-white/80 md:leading-normal lg:text-lg lg:leading-9 xl:text-xl xl:leading-[2.5rem]">
                            {t('about.vehicles.description')}
                        </p>
                        <button
                            onClick={() =>
                                window.open(
                                    'https://wa.me/94768539635?text=Hello%20Pick%20Lanka!%20I%27m%20interested%20in%20booking%20a%20tour.',
                                    '_blank'
                                )
                            }
                            className="mt-5 rounded-full bg-gradient-to-r from-[#009CE6] to-[#96BA33] px-6 py-3 font-redHatDisplay text-sm font-bold text-white transition hover:opacity-90 md:text-base"
                        >
                            {t('about.vehicles.bookNow')}
                        </button>
                    </div>

                    <div className="w-full overflow-x-clip md:w-[70%] xl:w-[75%]">
                        {/* VEHICLES SLIDER */}
                        <>
                            {/* Desktop */}
                            <div className="hidden w-full md:block">
                                <Swiper
                                    modules={[Autoplay]}
                                    breakpoints={{
                                        768: { slidesPerView: 1.8 },
                                        1024: { slidesPerView: 2.0 },
                                        1280: { slidesPerView: 2.5 },
                                    }}
                                    autoplay={{ delay: 3000, disableOnInteraction: false }}
                                    style={{ paddingBottom: 32 }}
                                >
                                    {vehicles.map((vehicle) => (
                                        <SwiperSlide key={vehicle.type}>
                                            <div className="relative h-[450px] w-full overflow-clip rounded-[20px] bg-white lg:h-[550px] xl:h-[680px]">
                                                <div className="absolute left-0 top-0 flex h-[40px] w-[75px] items-center justify-center bg-[#44891E] lg:h-[63px] lg:w-[120px] xl:h-[85px] xl:w-[160px]">
                                                    <span className="font-redHatDisplay text-sm font-bold text-white lg:text-xl xl:text-2xl">
                                                        {vehicle.type}
                                                    </span>
                                                </div>
                                                <img src={vehicle.image} alt={vehicle.type} className="h-[240px] w-full object-cover lg:h-[372px] xl:h-[500px]" />
                                                <div className="flex items-center justify-between gap-5 p-4 lg:gap-8 xl:gap-10 xl:p-5">
                                                    <p className="font-redHatDisplay text-base font-medium text-black lg:text-base xl:text-lg">
                                                        {vehicle.description}
                                                    </p>

                                                </div>
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>

                            {/* Mobile Stacked */}
                            <div className="mt-6 flex w-full flex-col items-center gap-6 md:mt-0 md:hidden">
                                {vehicles.map((vehicle) => (
                                    <div key={vehicle.type} className="w-full">
                                        <div className="relative h-[588px] w-full overflow-clip rounded-[20px] bg-white">
                                            <div className="absolute left-0 top-0 flex h-[70px] w-[150px] items-center justify-center bg-[#44891E]">
                                                <span className="font-redHatDisplay text-2xl font-bold text-white">{vehicle.type}</span>
                                            </div>
                                            <img src={vehicle.image} alt={vehicle.type} className="h-[428px] w-full object-cover" />
                                            <div className="flex items-center justify-between gap-10 p-5">
                                                <p className="font-redHatDisplay text-base font-medium text-black">{vehicle.description}</p>

                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default About;